import { useState } from 'react'
import { can } from '../../lib/api'
import type { LegalHold } from '../../lib/exit'
import { personLabel, usePeopleNames } from '../../lib/people'
import { useSave } from '../../lib/save'
import { ErrorBox } from '../DataTable'
import { placeHoldFooter, releaseHoldFooter } from '../footers'
import { Dialog, Status, Toggletip } from '../kit'
import { useKept } from './hooks'
import { dayMonthYear, stamp, truncate } from './words'

// Legal holds on a vendor's data, compact.
//
// A hold is what stops the destruction of a leaving vendor's data being
// recorded, so it sits beside the evidence it blocks. Placing and releasing
// each open a dialog; releasing needs somebody other than whoever placed it,
// which the API refuses in words if the wrong person tries.

export function LegalHoldsPanel({
  vendorId,
  holds,
  error,
  onChanged,
}: {
  vendorId: string
  holds: LegalHold[] | null
  error: unknown
  onChanged: () => void
}) {
  const [placing, setPlacing] = useState(false)
  const [releasing, setReleasing] = useState<LegalHold | null>(null)
  const names = usePeopleNames()
  // legal.hold since release 3.1 (package T3): legal counsel, the DPO and the
  // TPRM Admin place and release holds; it was the erasure capability before.
  const mayHold = can('legal.hold')
  const standing = (holds ?? []).filter((h) => h.standing)

  return (
    <section className="panel exit-panel" aria-labelledby="exit-holds-h">
      <div className="exit-panel-head">
        <h2 id="exit-holds-h" className="exit-grow">
          Legal holds
        </h2>
        {holds !== null && (
          <Status tone={standing.length > 0 ? 'special' : 'plain'}>
            {standing.length > 0 ? standing.length + ' standing' : 'None'}
          </Status>
        )}
        <Toggletip label="About legal holds">
          While a hold stands, destroying this vendor's data cannot be recorded. Record the retention under the hold
          instead.
        </Toggletip>
      </div>

      {error != null && <ErrorBox error={error} what="The legal holds" />}
      {holds !== null && holds.length > 0 && (
        <ul className="exit-holds">
          {holds.map((h) => (
            <li key={h.id}>
              <span className="exit-hold-what" title={h.reason}>
                {truncate(h.reason, 48)}
                <span className="exit-cell-sub">
                  {h.reference ? h.reference + ', ' : ''}
                  {dayMonthYear(h.placedAt)}
                </span>
              </span>
              <Status tone={h.standing ? 'special' : 'plain'}>{h.standing ? 'Standing' : 'Released'}</Status>
              <button type="button" className="exit-link" onClick={() => setReleasing(h)}>
                {h.standing && mayHold ? 'Release' : 'View'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {mayHold && (
        <button type="button" className="exit-link" onClick={() => setPlacing(true)}>
          Place a hold
        </button>
      )}

      <PlaceHoldDrawer
        vendorId={vendorId}
        open={placing}
        onClose={() => setPlacing(false)}
        onDone={() => {
          setPlacing(false)
          onChanged()
        }}
      />
      <HoldDrawer
        vendorId={vendorId}
        hold={releasing}
        mayRelease={mayHold}
        label={(s) => personLabel(names, s)}
        onClose={() => setReleasing(null)}
        onDone={() => {
          setReleasing(null)
          onChanged()
        }}
      />
    </section>
  )
}

function PlaceHoldDrawer({ vendorId, open, onClose, onDone }: { vendorId: string; open: boolean; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('')
  const [reference, setReference] = useState('')
  const save = useSave(open, 'Place a legal hold')
  const [err, setErr] = useState<unknown>(null)



  return (
    <Dialog
      shape="modal"
      kind="form"
      open={open}
      onClose={onClose}
      title="Place a legal hold"
      {...placeHoldFooter({ vendorId, reason, reference, busy: save.busy }, (work) => {
        setErr(null)
        save.send(work, {
          done: () => {
            setReason('')
            setReference('')
            onDone()
          },
          failed: setErr,
        })
      })}
    >
      <div className="exit-drawer-stack">
        {err != null && <ErrorBox error={err} what="The legal hold" verb="placed" />}
        <div className="field">
          <label htmlFor="hold-reason">Why the data has to be kept</label>
          <input id="hold-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="hold-ref">Matter or reference</label>
          <input id="hold-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <p className="exit-sub">Releasing it later needs somebody else</p>
      </div>
    </Dialog>
  )
}

function HoldDrawer({
  vendorId,
  hold: current,
  mayRelease,
  label,
  onClose,
  onDone,
}: {
  vendorId: string
  hold: LegalHold | null
  mayRelease: boolean
  label: (s: string) => string
  onClose: () => void
  onDone: () => void
}) {
  const hold = useKept(current)
  const [reason, setReason] = useState('')
  const save = useSave(current ? current.id : null, 'Legal hold')
  const [err, setErr] = useState<unknown>(null)
  const canRelease = !!hold && hold.standing && mayRelease



  // One hold and what is recorded about it: a card, whoever is looking. The
  // field and the button to release it are there for whoever may.
  return (
    <Dialog
      shape="modal"
      kind="record"
      open={current !== null}
      onClose={onClose}
      title="Legal hold"
      {...releaseHoldFooter({ vendorId, holdId: hold?.id ?? '', reason, busy: save.busy, mayRelease: canRelease }, (work) => {
        setErr(null)
        save.send(work, {
          done: () => {
            setReason('')
            onDone()
          },
          failed: setErr,
        })
      })}
    >
      {hold && (
        <div className="exit-drawer-stack">
          <Status tone={hold.standing ? 'special' : 'plain'}>{hold.standing ? 'Standing' : 'Released'}</Status>
          <p className="exit-para">{hold.reason}</p>
          <dl className="exit-kv">
            {hold.reference && (
              <div>
                <dt>Reference</dt>
                <dd>{hold.reference}</dd>
              </div>
            )}
            <div>
              <dt>Placed</dt>
              <dd>
                {label(hold.placedBy)}, {stamp(hold.placedAt)}
              </dd>
            </div>
            {hold.releasedAt && (
              <div>
                <dt>Released</dt>
                <dd>
                  {label(hold.releasedBy ?? '')}, {stamp(hold.releasedAt)}
                </dd>
              </div>
            )}
            <div>
              <dt>Hold id</dt>
              <dd className="exit-mono">{hold.id}</dd>
            </div>
          </dl>
          {hold.releaseReason && <p className="exit-para">{hold.releaseReason}</p>}
          {canRelease && (
            <>
              {err != null && <ErrorBox error={err} what="The legal hold" verb="released" />}
              <div className="field">
                <label htmlFor="hold-release">Why it is released</label>
                <input id="hold-release" type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
              <p className="exit-sub">Needs somebody other than whoever placed it</p>
            </>
          )}
        </div>
      )}
    </Dialog>
  )
}
